import React, { useEffect, useState } from 'react';
import { translate } from '@data/translations';
import { Reservation } from '../resController';
import { BMSSrcCardCodeModel } from '@common/modelDefinition';
import { addOrUpdateReservationCardCode, getReservationCardCodes } from '../api';
import useStore from '@data/state/zustand';
import { startGlobalLoading, stopGlobalLoading } from '@components/loaders/globalLoading';
import { fetchModuleHWAccessCodes, getRoomAllModulesMap } from '@pages/hw2/api';
import { CustomDayPicker } from '@components/DayPicker';
import TimePicker from '@components/timePicker';
import dayjs from 'dayjs';
import { ReactComponent as DeleteIcon } from '../../../assets/custom/icons/trash.svg';
interface ResRoomAccessCodesProps {
    reservation: Reservation;
    history: any;
}

const ResRoomAccessCodes: React.FC<ResRoomAccessCodesProps> = ({ reservation }) => {
    const { roomInfoId } = reservation;
    const loggedUser = useStore((appState) => appState.authTokenInfo?.user);

    const [loading, setLoading] = useState(true);
    const [cardCodes, setCardCodes] = useState<BMSSrcCardCodeModel[]>([]);
    const [srcHwModule, setSRCHwModule] = useState(false);
    const [newCardCode, setNewCardCode] = useState('');
    const [modified, setModified] = useState<Record<number, boolean>>({});
    const [HWAccessCodes, setHWAccessCodes] = useState<number[]>([]);
    const [showConnectionError, setShowConnectionError] = useState(false);
    const [duplicateCodes, setDuplicateCodes] = React.useState<Record<string, boolean>>({});

    const BMSNodeHWModules = useStore.getState().model.BMSNodeHWModule;

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            if (reservation?.id) {
                const roomAllModuleMap = await getRoomAllModulesMap(roomInfoId);
                const filteredModules = BMSNodeHWModules?.filter((module: any) =>
                    roomAllModuleMap?.some((roomModule) => roomModule?.bmsNodeHWModuleId === module?.id)
                );
                const filteredOnlyMxSRCv1 = filteredModules?.filter((module: any) => module?.type === 'mxSRCv1');
                const hasSRCv1Module = filteredModules?.some((module: any) => module?.type === 'mxSRCv1');
                setSRCHwModule(hasSRCv1Module);
                if (hasSRCv1Module) {
                    const hwAccessCodes: number[] = [];
                    for (const hwModule of filteredOnlyMxSRCv1) {
                        const hwCardCodes = await fetchModuleHWAccessCodes(hwModule.hwModuleUUID, hwModule.BMSNodeUUID);
                        for (const hwCardCode of hwCardCodes) {
                            if (hwCardCode.code !== 0) {
                                hwAccessCodes.push(hwCardCode.code);
                            }
                        }
                    }
                    setShowConnectionError(false);
                    setHWAccessCodes(hwAccessCodes);
                    const reservationCardCodes = await getReservationCardCodes(reservation.id, 'guest');
                    setCardCodes(reservationCardCodes);

                    const newDuplicateCodes: Record<number, boolean> = {};

                    for (let i = 0; i < reservationCardCodes.length; i++) {
                        const dataA = reservationCardCodes[i];
                        newDuplicateCodes[i] = reservationCardCodes.some(
                            (dataB: BMSSrcCardCodeModel, j: BMSSrcCardCodeModel) =>
                                dataA.cardCode !== 0 && dataA.cardCode === dataB.cardCode
                        );
                    }

                    setDuplicateCodes(newDuplicateCodes);
                }
            }
        } catch (err) {
            console.error('Error loading room card codes:', err);
            setShowConnectionError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleAddOrEditCardCode = async (roomInfoId: number) => {
        if (!newCardCode) return;

        try {
            startGlobalLoading();
            await addOrUpdateReservationCardCode({
                cardCode: Number(newCardCode),
                cardCodeValidFrom: reservation.checkInTimestamp,
                cardCodeValidTo: reservation.checkOutTimestamp,
                type: 'guest',
                roomInfoId,
                reservationId: reservation.id,
                statusEnum: 0,
                failedAttempts: 0,
                lastUpdatedBy: loggedUser?.uuid || '',
            });
            setNewCardCode('');

            await loadData();
            stopGlobalLoading();
        } catch (err) {
            stopGlobalLoading();
            console.error('Error adding/edit card code:', err);
        }
    };

    const removeCardCode = (cardCode: number) => {
        const currentTime = new Date().getTime();
        setCardCodes((prev) =>
            prev.map((code) =>
                code.cardCode === cardCode
                    ? {
                          ...code,
                          cardCodeValidTo: currentTime,
                          statusEnum: 0,
                          lastUpdatedBy: loggedUser?.uuid,
                      }
                    : code
            )
        );
        setModified((prev) => ({ ...prev, [cardCode]: true }));
    };

    const updateCardCode = async (cardCode: BMSSrcCardCodeModel) => {
        try {
            startGlobalLoading();
            await addOrUpdateReservationCardCode({
                ...cardCode,
                lastUpdatedBy: loggedUser?.uuid || '',
                statusEnum: 0,
                failedAttempts: 0,
            });
            setModified((prev) => ({ ...prev, [cardCode.cardCode]: false }));
            stopGlobalLoading();
        } catch (err) {
            stopGlobalLoading();
            console.error('Error updating card code:', err);
        }
    };

    const handleDateFromChange = (cardCode: number, date: Date) => {
        setCardCodes((prev) =>
            prev.map((code) => (code.cardCode === cardCode ? { ...code, cardCodeValidFrom: date.getTime() } : code))
        );
        setModified((prev) => ({ ...prev, [cardCode]: true }));
    };

    const handleDateToChange = (cardCode: number, date: Date) => {
        setCardCodes((prev) =>
            prev.map((code) => (code.cardCode === cardCode ? { ...code, cardCodeValidTo: date.getTime() } : code))
        );
        setModified((prev) => ({ ...prev, [cardCode]: true }));
    };

    const roundToNearest15 = (date = new Date()) => {
        const minutes = 15;
        const ms = 1000 * 60 * minutes;
        return new Date(Math.floor(date.getTime() / ms) * ms);
    };

    const handleTimeFromChange = (cardCode: number, time: any) => {
        const selectedCode = cardCodes.find((c) => c.cardCode === cardCode);
        if (!selectedCode) return;
        if (time) {
            const newDate = new Date(selectedCode.cardCodeValidFrom);
            newDate.setHours(time.hour());
            newDate.setMinutes(time.minute());

            setCardCodes((prev) =>
                prev.map((code) =>
                    code.cardCode === cardCode ? { ...code, cardCodeValidFrom: newDate.getTime() } : code
                )
            );
            setModified((prev) => ({ ...prev, [cardCode]: true }));
        }
    };

    const handleTimeToChange = (cardCode: number, time: any) => {
        const selectedCode = cardCodes.find((c) => c.cardCode === cardCode);
        if (!selectedCode) return;
        if (time) {
            const newDate = new Date(selectedCode.cardCodeValidTo);
            newDate.setHours(time.hour());
            newDate.setMinutes(time.minute());

            setCardCodes((prev) =>
                prev.map((code) =>
                    code.cardCode === cardCode ? { ...code, cardCodeValidTo: newDate.getTime() } : code
                )
            );
            setModified((prev) => ({ ...prev, [cardCode]: true }));
        }
    };

    if (loading) return <div className="loading-message">Loading...</div>;

    if (showConnectionError) {
        return (
            <div className="alert alert-danger" role="alert">
                {translate('HW MODULE COMMUNICATION ERROR!')}
            </div>
        );
    }

    if (!srcHwModule) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#ff0000' }}>
                {translate('No SRC controllers are connected to the room.')}
            </div>
        );
    }

    return (
        <div className="reservation-inner-height">
            <div className="add-code-container ml-8p mb-8p">
                <input
                    type="number"
                    className="input input-modal w-50"
                    value={newCardCode}
                    onChange={(e) => setNewCardCode(e.target.value)}
                    placeholder={translate('Enter new code')}
                />
                <button
                    className="button-success"
                    onClick={() => handleAddOrEditCardCode(roomInfoId)}
                    disabled={!newCardCode}
                >
                    {translate('Save')}
                </button>
            </div>
            <div className="custom-list-header ml-4p mr-4p">
                <div style={{ minWidth: '25%', maxWidth: '25%' }}>{translate('Code')}</div>
                <div style={{ minWidth: '40%', maxWidth: '40%' }}>{translate('Valid Period')}</div>
                <div style={{ minWidth: '30%', maxWidth: '30%' }}>{translate('Actions')}</div>
            </div>
            {cardCodes?.map((cardCode) => {
                const isModified = modified[cardCode.cardCode];
                const validFromDate = new Date(cardCode.cardCodeValidFrom);
                const validToDate = new Date(cardCode.cardCodeValidTo);
                const isValid =
                    HWAccessCodes.includes(cardCode.cardCode) &&
                    cardCode.statusEnum === 1 &&
                    new Date().getTime() > cardCode.cardCodeValidFrom &&
                    new Date().getTime() < cardCode.cardCodeValidTo;

                const _dateValue = new Date(cardCode.cardCodeValidTo);
                const _today = new Date();

                const isDateValueToday =
                    _dateValue.getFullYear() === _today.getFullYear() &&
                    _dateValue.getMonth() === _today.getMonth() &&
                    _dateValue.getDate() === _today.getDate();

                const isTodaysTimePassed = isDateValueToday && cardCode.cardCodeValidTo < Date.now();

                return (
                    <div className="display-flex flex-column mb-8p" key={cardCode.cardCode}>
                        <div className="custom-list-item ml-4p mr-4p">
                            <div className="flex-center font-16p" style={{ minWidth: '25%', maxWidth: '25%' }}>
                                {' '}
                                {cardCode.cardCode}
                                <span className={`status-dot ${isValid ? 'valid' : 'invalid'}`}></span>
                            </div>
                            <div className="display-flex flex-column" style={{ minWidth: '40%', maxWidth: '40%' }}>
                                <div className="flex-center mt-8p">
                                    <div className="w-25">{translate('From')}:</div>
                                    <div className="mr-4p" style={{ fontSize: '16px' }}>
                                        <CustomDayPicker
                                            value={new Date(validFromDate).getTime()}
                                            onSelect={(date: Date) => handleDateFromChange(cardCode.cardCode, date)}
                                            disabledBefore={new Date()}
                                            usePortal={true}
                                            fromYear={new Date().getFullYear()}
                                            toYear={new Date().getFullYear() + 100}
                                        />
                                    </div>
                                    <TimePicker
                                        minuteStep={15}
                                        value={dayjs(roundToNearest15(new Date(validFromDate)))}
                                        onChange={(time: any) => handleTimeFromChange(cardCode.cardCode, time)}
                                    />
                                </div>
                                <div className="flex-center mt-8p">
                                    <div className="w-25">{translate('To')}:</div>
                                    <div className="mr-4p" style={{ fontSize: '16px' }}>
                                        <CustomDayPicker
                                            value={new Date(validToDate).getTime()}
                                            onSelect={(date: Date) => handleDateToChange(cardCode.cardCode, date)}
                                            disabledBefore={new Date(validFromDate)}
                                            usePortal={true}
                                            fromYear={new Date().getFullYear()}
                                            toYear={new Date().getFullYear() + 100}
                                        />
                                    </div>
                                    <TimePicker
                                        minuteStep={15}
                                        value={dayjs(roundToNearest15(new Date(validToDate)))}
                                        onChange={(time: any) => handleTimeToChange(cardCode.cardCode, time)}
                                    />
                                </div>{' '}
                            </div>
                            <div className="flex-center mr-0 pr-0" style={{ minWidth: '30%', maxWidth: '30%' }}>
                                {reservation && (
                                    <button
                                        onClick={() => {
                                            const reservationTime = new Date(reservation.checkOutTimestamp);
                                            handleDateToChange(cardCode.cardCode, reservationTime);
                                        }}
                                        className="btn btn-sm btn-warning accessCodes1-icon-btn mr-8p"
                                        title={translate('Accept current reservation expiration')}
                                        style={{ width: '32px', height: '32px' }}
                                    >
                                        <i className="fa fa-calendar-check-o"></i>
                                    </button>
                                )}
                                <button
                                    disabled={!isModified}
                                    onClick={() => updateCardCode(cardCode)}
                                    className={isModified ? 'button-success' : 'button-secondary'}
                                    style={{height: "32px"}}
                                >
                                    {translate('Update')}
                                </button>
                                <div className="fill-red" onClick={() => removeCardCode(cardCode.cardCode)}>
                                    <DeleteIcon />
                                </div>
                            </div>
                        </div>
                        <div className='ml-8p'>
                            <div className="">
                                {duplicateCodes[cardCode.cardCode] ? (
                                    <span className="text-danger">
                                        {translate('This card code is already in use')}.
                                    </span>
                                ) : null}
                            </div>
                            {!isTodaysTimePassed && (
                                <span>
                                    {isDateValueToday
                                        ? translate(
                                              'Card code expires today. Make sure to check the expiration time to avoid losing access'
                                          ) + '.'
                                        : ''}
                                </span>
                            )}
                            <span>
                                {isTodaysTimePassed
                                    ? translate('Card code has expired. Please choose a new expiration time') + '.'
                                    : ''}
                            </span>{' '}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ResRoomAccessCodes;
